import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PartialDeep } from 'type-fest';
import type { Filter, Sort } from 'contexts/preferences/listing-params/types';
import type { MatchSpan, ParsedSearchFilters } from 'utils/natural-language-filter-parser/types';
import { parseNaturalLanguageFilters } from 'utils/natural-language-filter-parser';
import { useUndoableState } from 'hooks/use-undoable-state';
import useSmartTagSuggestions from 'hooks/use-smart-tag-suggestions';
import type { SmartTagSuggestionsStatus } from 'hooks/use-smart-tag-suggestions';
import { PARSE_DEBOUNCE_MS, RESET_FILTER_PATCH } from './smart-search.constants';
import {
  buildSmartSearchTokens,
  extractLocation,
  hasApplicableFilters,
  removeTokenFromFilters,
  removeTokenTextFromQuery,
  toFilterPatch,
  toSortValue,
} from './smart-search.utils';
import type {
  SmartSearchLocation,
  SmartSearchStatus,
  SmartSearchTagMatch,
  SmartSearchTokenModel,
} from './smart-search.types';

type ParseFn = typeof parseNaturalLanguageFilters;

/** The outcome of the most recent completed parse, tagged with its query. */
interface ParseSnapshot {
  query: string;
  filters: ParsedSearchFilters;
  /** Where each value was read from, so a dismissed chip can edit the query. */
  spans: readonly MatchSpan[];
  tags: string[] | null;
  unparsed: string | null;
  confidence: number | null;
  failed: boolean;
}

const EMPTY_FILTERS: ParsedSearchFilters = {};

/**
 * True when this edit begins a new word: the text so far ends at a word break
 * (or is empty) and the edit adds something that is not itself a break.
 *
 * Used to place undo checkpoints. Everything typed within one word collapses
 * into a single entry, so undo steps back a word instead of a character, and
 * deleting inside a word does not pile up entries either.
 */
function isStartingWord(previous: string, next: string): boolean {
  if (next.length <= previous.length) {
    return false;
  }
  const isAtWordBreak = previous.length === 0 || /\s$/.test(previous);
  return isAtWordBreak && /\S/.test(next.slice(previous.length));
}

const EMPTY_SPANS: readonly MatchSpan[] = [];

/** Stable default so an omitted `matchedTags` does not re-run effects per render. */
const EMPTY_TAGS: string[] = [];

const EMPTY_SNAPSHOT: ParseSnapshot = {
  query: '',
  filters: EMPTY_FILTERS,
  spans: EMPTY_SPANS,
  tags: null,
  unparsed: null,
  confidence: null,
  failed: false,
};

const parseSnapshotCache = new Map<string, ParseSnapshot>();
const PARSE_CACHE_LIMIT = 50;

function parseCacheKey(locale: string, trimmedQuery: string): string {
  return `${locale}\u0000${trimmedQuery}`;
}

function readCachedSnapshot(locale: string, trimmedQuery: string): ParseSnapshot | undefined {
  return trimmedQuery ? parseSnapshotCache.get(parseCacheKey(locale, trimmedQuery)) : undefined;
}

function writeCachedSnapshot(locale: string, snapshot: ParseSnapshot): void {
  if (!snapshot.query) {
    return;
  }
  const key = parseCacheKey(locale, snapshot.query);
  // Re-insert so the most recently used key is the newest (evict oldest first).
  parseSnapshotCache.delete(key);
  parseSnapshotCache.set(key, snapshot);
  if (parseSnapshotCache.size > PARSE_CACHE_LIMIT) {
    const oldest = parseSnapshotCache.keys().next().value;
    if (oldest !== undefined) {
      parseSnapshotCache.delete(oldest);
    }
  }
}

const matchedTagsCache = new Map<string, string[]>();

function readCachedMatchedTags(locale: string, trimmedQuery: string): string[] | undefined {
  return trimmedQuery ? matchedTagsCache.get(parseCacheKey(locale, trimmedQuery)) : undefined;
}

function writeCachedMatchedTags(locale: string, trimmedQuery: string, tags: string[]): void {
  if (!trimmedQuery) {
    return;
  }
  const key = parseCacheKey(locale, trimmedQuery);
  matchedTagsCache.delete(key);
  matchedTagsCache.set(key, tags);
  if (matchedTagsCache.size > PARSE_CACHE_LIMIT) {
    const oldest = matchedTagsCache.keys().next().value;
    if (oldest !== undefined) {
      matchedTagsCache.delete(oldest);
    }
  }
}

export interface UseSmartSearchStateOptions {
  onApplyFilters: (filter: PartialDeep<Filter>) => void;
  onApplySort?: (sort: Sort) => void;
  locale?: string;
  initialQuery?: string;
  initialTags?: string[];
  initialFilters?: ParsedSearchFilters;
  debounceMs?: number;
  parse?: ParseFn;
}

export interface SmartSearchState {
  query: string;
  onQueryChange: (value: string) => void;
  status: SmartSearchStatus;
  tokens: SmartSearchTokenModel[];
  location: SmartSearchLocation;
  unparsed: string | null;
  confidence: number | null;
  tags: string[] | null;
  removeToken: (tokenId: string) => void;
  smartTags: string[];
  matchedTags: string[];
  matchedTagDetails: SmartSearchTagMatch[];
  smartTagStatus: SmartTagSuggestionsStatus;
  addTag: (tag: string) => void;
  removeTag: (tag: string) => void;
  parsedFilters: ParsedSearchFilters;
  filterPatch: PartialDeep<Filter>;
  apply: () => void;
  clear: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True when there is nothing worth applying. */
  isApplyDisabled: boolean;
  hasApplicableQuery: boolean;
  isResolving: boolean;
}

/**
 * Owns the smart-search query, its parse, and the resulting `Filter` patch.
 *
 * Parsing is debounced and out-of-order responses are discarded. Everything the
 * UI reads is derived from the last completed parse plus the current query
 */
export function useSmartSearchState({
  onApplyFilters,
  onApplySort,
  locale = 'en-CA',
  initialQuery = '',
  initialTags = EMPTY_TAGS,
  initialFilters = EMPTY_FILTERS,
  debounceMs = PARSE_DEBOUNCE_MS,
  parse = parseNaturalLanguageFilters,
}: UseSmartSearchStateOptions): SmartSearchState {
  const history = useUndoableState(initialQuery);
  const { value: query, set, replace, undo: undoHistory, redo: redoHistory } = history;

  const trimmedQuery = query.trim();
  const isEmptyQuery = trimmedQuery.length === 0;

  const [snapshot, setSnapshot] = useState<ParseSnapshot>(
    () => readCachedSnapshot(locale, initialQuery.trim()) ?? EMPTY_SNAPSHOT
  );
  const [removedTokenIds, setRemovedTokenIds] = useState<string[]>([]);
  const [baseFilters, setBaseFilters] = useState<ParsedSearchFilters>(initialFilters);
  const [lastApplied, setLastApplied] = useState<ParsedSearchFilters>(initialFilters);
  const [manualTags, setManualTags] = useState<string[]>(initialTags);
  const [removedTags, setRemovedTags] = useState<string[]>([]);
  const [lastAppliedTags, setLastAppliedTags] = useState<string[]>(initialTags);

  const unparsedQuery = isEmptyQuery ? '' : (snapshot.unparsed ?? '');

  const { matches: smartTagMatches, status: smartTagStatus } = useSmartTagSuggestions(unparsedQuery, { debounceMs });
  const liveMatchedTags = useMemo(
    () => smartTagMatches.map(match => match.tagName),
    [smartTagMatches]
  );
  const matchedTagDetails = useMemo<SmartSearchTagMatch[]>(
    () => smartTagMatches.map(match => ({ tagName: match.tagName, maxMatchSim: match.maxMatchSim })),
    [smartTagMatches]
  );

  // Cache the matches once the fetch settles so a later reopen can seed them.
  useEffect(() => {
    if (smartTagStatus === 'ready') {
      writeCachedMatchedTags(locale, unparsedQuery, liveMatchedTags);
    }
  }, [smartTagStatus, locale, unparsedQuery, liveMatchedTags]);

  const matchedTags = useMemo(() => {
    if (smartTagStatus === 'ready') {
      return liveMatchedTags;
    }
    return readCachedMatchedTags(locale, unparsedQuery) ?? liveMatchedTags;
  }, [smartTagStatus, liveMatchedTags, locale, unparsedQuery]);

  const matchedTagsKey = matchedTags.join('\u0000');
  const [dismissedForKey, setDismissedForKey] = useState(matchedTagsKey);
  if (matchedTagsKey !== dismissedForKey) {
    setDismissedForKey(matchedTagsKey);
    setRemovedTags(current => (current.length === 0 ? current : []));
  }

  const smartTags = useMemo(() => {
    const removed = new Set(removedTags);
    const result: string[] = [];
    for (const tag of [...matchedTags, ...manualTags]) {
      if (!removed.has(tag) && !result.includes(tag)) {
        result.push(tag);
      }
    }
    return result;
  }, [matchedTags, manualTags, removedTags]);

  const requestIdRef = useRef(0);

  const isSnapshotCurrent = snapshot.query === trimmedQuery;

  useEffect(() => {
    if (isEmptyQuery || isSnapshotCurrent) {
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const timeoutId = setTimeout(() => {
      parse(trimmedQuery)
        .then(result => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setRemovedTokenIds([]);
          const nextSnapshot: ParseSnapshot = {
            query: trimmedQuery,
            filters: result?.filters ?? EMPTY_FILTERS,
            spans: result?.spans ?? EMPTY_SPANS,
            tags: result?.tags ?? null,
            unparsed: result ? result.unparsed : trimmedQuery,
            confidence: result?.metrics.confidence ?? null,
            failed: false,
          };
          // Cache the completed parse so reopening this query is instant.
          writeCachedSnapshot(locale, nextSnapshot);
          setSnapshot(nextSnapshot);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setSnapshot({ ...EMPTY_SNAPSHOT, query: trimmedQuery, failed: true });
        });
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [trimmedQuery, isEmptyQuery, isSnapshotCurrent, debounceMs, parse, locale]);

  let status: SmartSearchStatus = 'ready';
  if (isEmptyQuery) {
    status = 'idle';
  } else if (!isSnapshotCurrent) {
    status = 'parsing';
  } else if (snapshot.failed) {
    status = 'error';
  }

  const isSettled = !isEmptyQuery && isSnapshotCurrent;
  const isReadyToApply = isEmptyQuery || isSnapshotCurrent;

  const filters = useMemo(() => {
    const merged = isEmptyQuery ? baseFilters : { ...baseFilters, ...snapshot.filters };
    return removedTokenIds.reduce(removeTokenFromFilters, merged);
  }, [isEmptyQuery, baseFilters, snapshot.filters, removedTokenIds]);

  const tokens = useMemo(() => buildSmartSearchTokens(filters, locale), [filters, locale]);
  const location = useMemo(() => extractLocation(filters), [filters]);

  const onQueryChange = useCallback((value: string) => {
    if (value.trim().length === 0 && query.trim().length > 0) {
      setManualTags(EMPTY_TAGS);
      setRemovedTags(EMPTY_TAGS);
    }
    // Checkpoint as a word begins, then edit that word in place. One undo then
    // steps back a word: the entry it restores is the text as it stood before
    // the word was started, which is where the previous word left off.
    if (isStartingWord(query, value)) {
      set(value);
      return;
    }
    replace(value);
  }, [query, replace, set]);

  const removeToken = useCallback((tokenId: string) => {
    // Both halves are needed. Editing the query is the real removal — it stops
    // the next parse from reading the filter straight back in — but it re-parses
    // on a debounce, so the id is also held aside to drop the chip immediately
    // instead of leaving it on screen until the parse catches up.
    setRemovedTokenIds(current => (current.includes(tokenId) ? current : [...current, tokenId]));
    const next = removeTokenTextFromQuery(query, tokenId, snapshot.spans);
    if (next !== query) {
      // Checkpointed, so a chip dismissed by accident is one undo away.
      set(next);
    }
  }, [query, set, snapshot.spans]);

  const addTag = useCallback((tag: string) => {
    setManualTags(current => (current.includes(tag) ? current : [...current, tag]));
    // Undo a prior dismissal so tapping a matched tag back in takes effect.
    setRemovedTags(current => current.filter(entry => entry !== tag));
  }, []);

  const removeTag = useCallback((tag: string) => {
    setManualTags(current => current.filter(entry => entry !== tag));
    // Dismiss the matched copy too, so it does not linger on screen.
    setRemovedTags(current => (current.includes(tag) ? current : [...current, tag]));
  }, []);

  const filterPatch = useMemo(() => {
    const patch = toFilterPatch(filters, lastApplied);
    if (smartTags.length > 0 || lastAppliedTags.length > 0) {
      patch.smartTagNames = smartTags;
    }
    return patch;
  }, [filters, lastApplied, smartTags, lastAppliedTags]);

  const apply = useCallback(() => {
    setLastApplied(filters);
    setLastAppliedTags(smartTags);
    if (Object.keys(filterPatch).length > 0) {
      onApplyFilters(filterPatch);
    }
    const sort = toSortValue(filters);
    if (sort) {
      onApplySort?.(sort);
    }
  }, [filterPatch, filters, smartTags, onApplyFilters, onApplySort]);

  const clear = useCallback(() => {
    const hadAppliedFilters = Object.keys(lastApplied).length > 0 || lastAppliedTags.length > 0;
    setBaseFilters(EMPTY_FILTERS);
    setLastApplied(EMPTY_FILTERS);
    setManualTags(EMPTY_TAGS);
    setRemovedTags(EMPTY_TAGS);
    setLastAppliedTags(EMPTY_TAGS);
    requestIdRef.current += 1;
    history.reset('');
    setRemovedTokenIds([]);
    setSnapshot(EMPTY_SNAPSHOT);
    if (hadAppliedFilters) {
      onApplyFilters(RESET_FILTER_PATCH);
    }
  }, [history, lastApplied, lastAppliedTags, onApplyFilters]);

  return {
    query,
    onQueryChange,
    status,
    tokens,
    location,
    unparsed: isSettled ? snapshot.unparsed : null,
    confidence: isSettled ? snapshot.confidence : null,
    tags: snapshot.tags,
    removeToken,
    smartTags,
    matchedTags,
    matchedTagDetails,
    smartTagStatus,
    addTag,
    removeTag,
    parsedFilters: isSettled ? snapshot.filters : EMPTY_FILTERS,
    filterPatch,
    apply,
    clear,
    undo: undoHistory,
    redo: redoHistory,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    isApplyDisabled: smartTags.length === 0 && (!isReadyToApply || !hasApplicableFilters(filters)),
    hasApplicableQuery: smartTags.length > 0 || (isSettled && hasApplicableFilters(snapshot.filters)),
    isResolving: status === 'parsing' || smartTagStatus === 'loading',
  };
}

export default useSmartSearchState;
